/**
 * Componente compartilhado para coletar a configuracao de fallback do destino
 * 'stasis' (App Stasis / voice-gateway-ai).
 *
 * Renderiza dois inputs: tipo do fallback (dropdown com 5 opcoes) e o destino
 * apropriado para o tipo escolhido. Espelha o subset seguro de tipos
 * (endpoint|queue|external|disa|hangup) — sem 'stasis' (recursao) e sem 'cycle'.
 *
 * Uso:
 *   {values.destination_type_main === 'stasis' && (
 *     <StasisFallbackInputs
 *       fallbackType={values.stasis_fallback_type}
 *       fallbackDestination={values.stasis_fallback_destination}
 *       onChange={(field, value) => setValues(p => ({ ...p, [field]: value }))}
 *       availableEndpoints={availableEndpoints}
 *       availableQueues={availableQueues}
 *       availableContexts={availableContexts}
 *       errorType={errors.stasis_fallback_type}
 *       errorDestination={errors.stasis_fallback_destination}
 *     />
 *   )}
 */

import React from 'react';

const FALLBACK_TYPES = [
  { value: 'endpoint', label: 'Ramal' },
  { value: 'queue',    label: 'Fila' },
  { value: 'external', label: 'Externo' },
  { value: 'disa',     label: 'DISA / URA / Mensagem' },
  { value: 'hangup',   label: 'Desligar' },
];

const StasisFallbackInputs = ({
  fallbackType = '',
  fallbackDestination = '',
  onChange,
  availableEndpoints = [],
  availableQueues = [],
  availableContexts = [],
  errorType,
  errorDestination,
  fieldNamePrefix = 'stasis_fallback',
}) => {
  const typeField = `${fieldNamePrefix}_type`;
  const destField = `${fieldNamePrefix}_destination`;

  const renderDestinationInput = () => {
    if (!fallbackType || fallbackType === 'hangup') {
      return null;
    }
    if (fallbackType === 'endpoint') {
      return (
        <div className="select is-fullwidth">
          <select
            value={fallbackDestination}
            onChange={(e) => onChange(destField, e.target.value)}
          >
            <option value="">Selecione um ramal</option>
            {availableEndpoints.map((ep) => (
              <option key={ep.endpoint || ep.id} value={ep.endpoint || ep.id}>
                {ep.endpoint || ep.id} {ep.callerid ? `- ${ep.callerid}` : ''}
              </option>
            ))}
          </select>
        </div>
      );
    }
    if (fallbackType === 'queue') {
      return (
        <div className="select is-fullwidth">
          <select
            value={fallbackDestination}
            onChange={(e) => onChange(destField, e.target.value)}
          >
            <option value="">Selecione uma fila</option>
            {availableQueues.map((q) => (
              <option key={q.name || q.id_callcenter_queue || q} value={q.name || q.id_callcenter_queue || q}>
                {q.name || q}
              </option>
            ))}
          </select>
        </div>
      );
    }
    if (fallbackType === 'disa') {
      return (
        <div className="select is-fullwidth">
          <select
            value={fallbackDestination}
            onChange={(e) => onChange(destField, e.target.value)}
          >
            <option value="">Selecione um contexto</option>
            {availableContexts.map((c) => (
              <option key={c.name || c} value={c.name || c}>
                {c.name || c}
              </option>
            ))}
          </select>
        </div>
      );
    }
    // external e default
    return (
      <input
        className="input"
        type="text"
        placeholder={fallbackType === 'external' ? 'numero@trunk' : 'Destino do fallback'}
        value={fallbackDestination}
        onChange={(e) => onChange(destField, e.target.value)}
        maxLength={255}
      />
    );
  };

  return (
    <div className="notification is-warning is-light" style={{ marginTop: '0.75rem' }}>
      <p style={{ marginBottom: '0.5rem', fontSize: '0.85rem' }}>
        <strong>Fallback obrigatorio:</strong> destino se o app Stasis voice-gateway-ai estiver indisponivel.
      </p>

      <div className="field">
        <label className="label is-small">Tipo do fallback *</label>
        <div className="select is-fullwidth">
          <select
            value={fallbackType}
            onChange={(e) => {
              const newType = e.target.value;
              onChange(typeField, newType);
              // hangup nao precisa de destino
              if (newType === 'hangup') {
                onChange(destField, '');
              }
            }}
          >
            <option value="">Selecione um tipo</option>
            {FALLBACK_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        {errorType && <p className="help is-danger">{errorType}</p>}
      </div>

      {fallbackType && fallbackType !== 'hangup' && (
        <div className="field">
          <label className="label is-small">Destino do fallback *</label>
          {renderDestinationInput()}
          {errorDestination && <p className="help is-danger">{errorDestination}</p>}
        </div>
      )}
    </div>
  );
};

export default StasisFallbackInputs;
